import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ArrowForward } from '@mui/icons-material';

import * as R from 'ramda';

import { connect } from 'react-redux';

import { withStyles } from '@mui/styles';
import { withTranslation } from 'react-i18next';

import * as moment from 'moment';
import { selectTimeSlot } from '../../store/actions/availabilityActions';
import CustomIcon from '../../utils/CustomIcon';
import { settings } from '../../sites';
import { getLanguageGatCode } from '../../utils/localeUtils';

const inlineStyles = theme => ({
  container: {
    borderTop: '1px solid rgba(205, 205, 205, 1)',
    padding: theme.spacing(0, 2),

    '&:last-of-type': {
      borderBottom: '1px solid rgba(205, 205, 205, 1)',
    },
  },
  button: {
    width: '100%',
    paddingTop: 12,
    paddingBottom: 12,
  },
  expansionPanel: {
    width: '100%',
  },
  arrow: {
    border: '1px solid',
    borderRadius: '4px',
    height: '34px',
    width: '34px',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  StartTime: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    fontSize: 16,
    textTransform: 'capitalize',
    [theme.breakpoints.up('md')]: {
      fontSize: 18,
    },
  },
  optician: {
    alignItems: 'center',
    display: 'flex',
  },
  icon: {
    height: '15px',
    marginRight: '11px',
  },
});

/**
 * SubService
 * Use to print out buttons that contain subservice information such as "Silmänpohjankuvaus"
 * @param {Object} classes     Classes from JSS inlineStyles
 * @param {Object} content     Content object (headline, caption, price)
 */

const AvailableTime = ({
  classes,
  content,
  // eslint-disable-next-line no-shadow
  selectTimeSlot,
  selectedOptician,
  selectedOpticianName,
  appointmentType,
  t,
  stores,
  storeId,
  day,
  lang,
  showOnlyStartTime,
}) => {
  const { startTime, endTime } = content;

  const storeDetails = R.find(R.propEq('id', storeId))(stores);

  const startTimeParsed = moment(startTime, 'YYYY-MM-DDTHH:mm');
  const endTimeParsed = moment(endTime, 'YYYY-MM-DDTHH:mm');

  const languageGatCode = getLanguageGatCode(lang);

  return (
    <Grid item container className={classes.container}>
      <ButtonBase
        disableRipple
        className={classes.button}
        onClick={() =>
          selectTimeSlot({
            timeSlot: startTimeParsed,
            endTime: endTimeParsed,
            day,
            timeSlotFormatted: startTimeParsed.format('HH:mm'),
            storeAddress: `${storeDetails.attributes.address.line1}, ${storeDetails.attributes.address.city}`,
            selectedStoreId: storeId,
            selectedOptician,
            selectedOpticianName,
            appointmentType,
            storeName: R.path(['attributes', 'name', languageGatCode], storeDetails),
          })
        }>
        <Grid direction="row" spacing={0} item container alignItems="center" justifyContent="space-between">
          <Grid item xs={10} md={8} align="left">
            <Grid direction="column" spacing={0} container>
              <Grid item>
                <Typography variant="body1" className={classes.StartTime}>
                  {showOnlyStartTime
                    ? `${startTimeParsed.format('HH:mm')}`
                    : `${startTimeParsed.format('HH:mm')}-${endTimeParsed.format('HH:mm')}`}
                </Typography>
                {showOnlyStartTime && (
                  <Typography variant="body1">{t('views.selectService.reserve-enough-time')}</Typography>
                )}
              </Grid>
              {get(settings, 'showOpticians') && selectedOpticianName !== '' && (
                <Grid item>
                  <Typography variant="body1" className={classes.optician}>
                    <CustomIcon className={classes.icon} icon="user" /> <strong>{selectedOpticianName}</strong>
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={1} md={2} align="right">
            <Box className={classes.arrow} sx={{ display: { md: 'none', xs: 'block' } }}>
              <ArrowForward />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Button disableRipple color="secondary" variant="contained" component={Box}>
                {t('views.availableTime.choose')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </ButtonBase>
    </Grid>
  );
};

AvailableTime.propTypes = {
  appointmentType: PropTypes.string,
  classes: PropTypes.object,
  content: PropTypes.object,
  day: PropTypes.string,
  lang: PropTypes.string,
  selectedOptician: PropTypes.any,
  selectedOpticianName: PropTypes.string,
  selectTimeSlot: PropTypes.any,
  stores: PropTypes.array,
  storeId: PropTypes.string,
  t: PropTypes.func,
  showOnlyStartTime: PropTypes.bool,
};

AvailableTime.defaultProps = {
  appointmentType: '',
  classes: {},
  content: {},
  day: '',
  lang: '',
  selectedOptician: '',
  selectedOpticianName: '',
  selectTimeSlot: null,
  stores: [],
  storeId: '',
  t: null,
  showOnlyStartTime: false,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  selectTimeSlot: values => dispatch(selectTimeSlot(values, R.path(['history'], ownProps), R.path(['lang'], ownProps))),
  lang: R.path(['lang'], ownProps),
});

export default withStyles(inlineStyles)(
  withTranslation('translations')(connect(null, mapDispatchToProps)(AvailableTime)),
);
